import * as React from 'react';

import {
    BLOCK_HEADINGS,
    BLOCK_REUSABLE_CONTENT,
    reusableContent,
    TURNABLE_INTO_REUSABLE_CONTENT_BLOCKS,
} from '@gitbook/doc-core';
import { createEdits, REUSABLE_CONTENT_TITLE_FALLBACK, randomUUID } from '@gitbook/hive-core';
import { useHiveBranch, useHiveClient } from '@gitbook/hive-react';
import { Block, type BlockRange, Document } from '@gitbook/slate';
import { type EditorContainer, useEditor } from '@gitbook/slate-react';
import { toast } from '@gitbook/spine-react';
import { assertIsDefined } from '@gitbook/util-ts';

import { useLoggedInUser } from '../../../state';
import { truncate } from '../../../utils/text';
import { useReusableContentDisclaimer } from '../../ReusableContent';
import { ContentEditorContext } from '../contexts';

const NO_SUPPORT = Object.freeze({ reason: 'no-support' as const });
const ONLY_ONE_REUSABLE_CONTENT = Object.freeze({ reason: 'only-one-reusable-content' as const });

/**
 * Check if a block range can be turned into reusable content.
 */
export function useCheckTurnableIntoReusableContent() {
    const {
        blocks: { withReusableContent },
    } = React.useContext(ContentEditorContext);
    return (input: { blockRange: BlockRange; editor: EditorContainer }) => {
        if (!withReusableContent) {
            return NO_SUPPORT;
        }
        const { editor, blockRange } = input;
        const blocks = blockRange.getBlocks(editor.value.document);
        const singleBlock = blocks.size === 1 ? blocks.first() : null;
        const hasOnlyOneReusableContentBlock = singleBlock?.type === BLOCK_REUSABLE_CONTENT;

        if (hasOnlyOneReusableContentBlock) {
            return ONLY_ONE_REUSABLE_CONTENT;
        }

        const unsupportedBlockTypes = blocks
            .filter((block) => !TURNABLE_INTO_REUSABLE_CONTENT_BLOCKS.has(block.type))
            .map((block) => block.type)
            .toArray();

        if (unsupportedBlockTypes.length > 0) {
            return {
                reason: 'unsupported-block-type' as const,
                unsupportedBlockTypes,
            };
        }
        return null;
    };
}

/**
 * Turn a block range into reusable content.
 */
export function useTurnIntoReusableContent() {
    const hiveBranch = useHiveBranch();
    const editor = useEditor();
    const user = useLoggedInUser();
    const { logAnalyticsEvent } = React.useContext(ContentEditorContext);
    const hiveClient = useHiveClient();
    const wrapAction = useReusableContentDisclaimer();

    return (input: {
        blockRange: BlockRange;
        method: 'editor-palette' | 'editor-prompt-on-copy';
    }) => {
        const { method, blockRange } = input;
        return wrapAction({
            mode: 'turnInto',
            callback: async () => {
                await toast.catch(async () => {
                    const blocks = blockRange.getBlocks(editor.value.document);
                    // Create a fragment of a document from the selected blocks.
                    const document = Document.create({
                        data: { schemaType: 'reusable-content' },
                        nodes: blocks.toArray(),
                    });
                    const sanitizedDocument = document.mapDescendants((node) => {
                        if (Block.isBlock(node) && node.data.has('completionId')) {
                            return node.removeData(['completionId']);
                        }
                        return node;
                    });

                    const id = randomUUID();

                    // Add the reusable content to the revision. We await this to ensure our
                    // block doesn't show a not found.
                    assertIsDefined(hiveBranch);
                    await hiveBranch.change((change) => {
                        change.insertReusableContent({
                            id,
                            title:
                                inferReusableContentTitle(sanitizedDocument) ||
                                REUSABLE_CONTENT_TITLE_FALLBACK,
                            edits: createEdits(user.uid),
                        });
                    });

                    // Connect to the HiveDocument of the reusable content and insert the document.
                    const reusableContentSync = hiveClient.sync.reusableContent(
                        hiveBranch.contentId,
                        hiveBranch.branchId,
                        id
                    );
                    await reusableContentSync.open();
                    reusableContentSync.change((change) => {
                        const first = change.value.document.nodes.first();
                        assertIsDefined(first);
                        change.collapseToStartOf(first);
                        change.insertDocument(sanitizedDocument);
                    });

                    // Replace the content in the editor with the new block.
                    editor.change((change) => {
                        change.select(input.blockRange);
                        change.insertBlock(
                            reusableContent.createReusableContentBlock({
                                kind: 'reusable-content',
                                reusableContent: id,
                            })
                        );
                    });
                    logAnalyticsEvent({
                        type: 'reusable_content_create',
                        method,
                    });
                }, 'Error while turning into synced block');
            },
        });
    };
}

/**
 * Infer a title for reusable content from a document.
 */
function inferReusableContentTitle(document: Document): string | undefined {
    const blocks = document.getBlocks();
    const firstBlock = blocks.first();
    if (!firstBlock) {
        return undefined;
    }
    if (BLOCK_HEADINGS.has(firstBlock.type)) {
        return truncate(firstBlock.text, 30);
    }
    return truncate(document.text, 30);
}
